import { useState } from "react";
import { toast } from "sonner";
import { Warehouse, Truck, Tag, TestTubes, ShoppingBag, FileText } from "lucide-react";

// Preview-only visual mockup of the SUPPLY page redesign. Static mock data; no cart/pricing logic.
const BURGUNDY = "#7A1526";
const INK = "#1F1A17";
const MOCK = [
  { slug: "glo", name: "GLO" },
  { slug: "klo", name: "KLO" },
  { slug: "mots-c", name: "MOTS-C" },
  { slug: "tz-2a", name: "TZ-2A" },
].map((p) => ({ ...p, img: `/mockups/supply-${p.slug}.png` }));
const BENEFITS = [
  { icon: Warehouse, label: "US-Warehoused RUO Product" },
  { icon: Truck, label: "3-5 Day Shipping" },
  { icon: Tag, label: "Bulk Pricing" },
  { icon: TestTubes, label: "3 & 10-Vial Kits" },
];
const TESTS = [
  { n: "01", title: "Purity Analysis", method: "HPLC/UV", desc: "Verifies purity and identifies impurities." },
  { n: "02", title: "Identity Confirmation", method: "LC-MS", desc: "Confirms identity against the expected compound." },
  { n: "03", title: "Quantity Verification", method: "Gravimetric + HPLC", desc: "Checks content against the labeled quantity." },
];
const mockOnly = () => toast("Preview mockup — controls are visual only.");

export default function SupplyRedesignMockup() {
  return (
    <div className="max-w-[1500px] mx-auto px-6 pt-14 pb-10" data-testid="supply-redesign-mockup">
      <Hero />
      <TestingPanel />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4" data-testid="mockup-grid">
        {MOCK.map((p) => <MockCard key={p.slug} product={p} />)}
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 mb-8">
      <div>
        <p className="font-mono uppercase text-[15px] tracking-[0.12em] mb-2" style={{ color: BURGUNDY }}>Supply</p>
        <h1 className="font-serif text-5xl lg:text-[56px] leading-tight" style={{ color: BURGUNDY }} data-testid="mockup-hero-title">
          Research Supply. Simplified.
        </h1>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-20 gap-y-3 lg:mr-36" data-testid="mockup-benefits">
        {BENEFITS.map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-center gap-4">
            <span className="h-12 w-12 rounded-md flex items-center justify-center" style={{ background: "#F6E2DC" }}>
              <Icon size={24} strokeWidth={1.6} style={{ color: BURGUNDY }} />
            </span>
            <span className="font-sans font-bold text-[16px]" style={{ color: INK }}>{label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function TestingPanel() {
  return (
    <section className="rounded-md border px-6 pt-2.5 pb-4" style={{ background: "#F5EFEA", borderColor: "#E2D8CF" }} data-testid="mockup-testing-panel">
      <p className="font-sans font-bold uppercase text-[22px] mb-2" style={{ color: BURGUNDY }}>
        3X Tested <span className="mx-3 font-normal">|</span> Triple-Verified Quality
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3">
        {TESTS.map((t, i) => (
          <div key={t.n} className={`flex gap-8 ${i > 0 ? "md:border-l md:pl-7" : ""} md:pr-6`} style={{ borderColor: "#DDD2C8" }}>
            <span className="font-serif font-bold text-[34px] leading-none" style={{ color: BURGUNDY }}>{t.n}</span>
            <div>
              <p className="font-sans font-bold uppercase text-[16px]" style={{ color: INK }}>{t.title}</p>
              <p className="font-mono uppercase text-[13px] tracking-[0.06em] mt-1" style={{ color: BURGUNDY }}>{t.method}</p>
              <p className="text-[14px] mt-3" style={{ color: "#6B625C" }}>{t.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function MockCard({ product }) {
  const [kit, setKit] = useState(3);
  return (
    <div className="rounded-md border p-4" style={{ background: "#FFFDFB", borderColor: "#E6DDD4" }} data-testid={`mockup-card-${product.slug}`}>
      <img src={product.img} alt={product.name} className="w-full aspect-[330/187] object-cover rounded-sm" draggable={false} />
      <h3 className="font-serif font-bold text-[32px] leading-none mt-3" style={{ color: BURGUNDY, fontVariantNumeric: "lining-nums", WebkitTextStroke: `0.4px ${BURGUNDY}` }} data-testid={`mockup-name-${product.slug}`}>{product.name}</h3>
      <p className="font-mono font-medium uppercase text-[13px] tracking-[0.2em] mt-2 pb-3 border-b" style={{ color: "#5E5650", borderColor: "#ECE5DE" }} data-testid={`mockup-ruo-${product.slug}`}>
        Research Use Only
      </p>
      <div className="grid grid-cols-2 gap-2 mt-4">
        {[3, 10].map((n) => (
          <KitButton key={n} n={n} active={kit === n} onClick={() => setKit(n)} slug={product.slug} />
        ))}
      </div>
      <div className="grid grid-cols-[1.65fr_1fr] gap-2 mt-3">
        <button onClick={mockOnly} className="h-12 rounded-full text-white font-sans font-bold uppercase text-[15px] flex items-center justify-center gap-3 transition-opacity hover:opacity-90" style={{ background: BURGUNDY }} data-testid={`mockup-add-${product.slug}`}>
          <ShoppingBag size={20} strokeWidth={1.8} /> Add · $105
        </button>
        <button onClick={mockOnly} className="h-12 rounded-full border font-sans font-bold uppercase text-[15px] flex items-center justify-center gap-2.5 bg-white border-[#CDBFB4] transition-colors duration-200 hover:bg-[#F6E6DF] hover:border-[#B89A8C]" style={{ color: BURGUNDY }} data-testid={`mockup-coa-${product.slug}`}>
          <FileText size={18} strokeWidth={1.8} /> COA
        </button>
      </div>
    </div>
  );
}

function KitButton({ n, active, onClick, slug }) {
  return (
    <button
      onClick={onClick}
      className="h-11 rounded-full border font-sans font-bold uppercase text-[14px] transition-colors"
      style={{
        color: active ? INK : "#3A3330",
        background: active ? "#F3E4D0" : "#F4F1EE",
        borderColor: active ? BURGUNDY : "#E1DAD3",
        borderWidth: active ? 1.5 : 1,
      }}
      data-testid={`mockup-kit-${n}-${slug}`}
    >
      {n} Vials
    </button>
  );
}
